import { useEffect, useRef, useState, type ReactNode } from 'react';
import clsx from 'clsx';
import { Trans, useTranslation } from 'react-i18next';
import { LanguageSwitcher } from './providers/I18nProvider';
import { appVersion } from '../shared/config/appVersion';
import { SEOHead } from '../shared/components/SEOHead';

import type { QuotaStatus } from '../features/style-transfer/state/sessionStore';

interface ProfileSummary {
  email?: string | null;
  userName?: string | null;
  quotaRemaining?: number | null;
  quotaTotal?: number | null;
  quotaStatus?: QuotaStatus;
}

interface AppShellProps {
  children: ReactNode;
  onLoginClick?: () => void;
  onVoucherRedeemClick?: () => void;
  onAddFundsClick?: () => void;
  onInviteClick?: () => void;
  isLoginActive?: boolean;
  isAuthenticated?: boolean;
  profile?: ProfileSummary;
  onSignOut?: () => void;
  guestQuotaRemaining?: number | null;
  guestQuotaStatus?: QuotaStatus;
}

/**
 * AppShell provides the global thematic wrapper, background treatments, and responsive constraints
 * required by the project constitution. It centralizes gradients, grid overlays, and spacing so
 * downstream feature pages automatically inherit the AI-driven visual language.
 */
export function AppShell({
  children,
  onLoginClick,
  onVoucherRedeemClick,
  onAddFundsClick,
  onInviteClick,
  isLoginActive,
  isAuthenticated = false,
  profile,
  onSignOut,
  guestQuotaRemaining,
  guestQuotaStatus
}: AppShellProps) {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isMenuOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isMenuOpen]);

  useEffect(() => {
    if (!isAuthenticated) {
      setIsMenuOpen(false);
    }
  }, [isAuthenticated]);

  const actionButtonClasses = clsx(
    'inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring focus-visible:ring-ai-focus',
    isLoginActive
      ? 'border-ai-primary/70 bg-ai-primary/20 text-ai-text-primary shadow-ai-glow'
      : 'border-ai-surface-glow/30 bg-ai-surface-elevated/70 text-ai-text-secondary hover:border-ai-primary/40 hover:text-ai-text-primary'
  );
  const voucherHighlightClasses =
    'inline-flex items-center rounded-full bg-gradient-to-r from-[#ff2d55] via-[#ff6f3c] to-[#ffd000] px-3 py-1 font-semibold uppercase tracking-[0.25em] text-black shadow-[0_12px_32px_-18px_rgba(255,45,85,0.85)]';

  const displayEmail = profile?.email ?? t('app.profile.unknown');
  const displayName =
    profile?.userName && profile.userName.trim().length > 0
      ? profile.userName
      : profile?.email
        ? profile.email.split('@')[0]
        : t('app.profile.unknown');

  const handleToggleMenu = () => {
    setIsMenuOpen((open) => !open);
  };

  const handleSignOut = () => {
    setIsMenuOpen(false);
    onSignOut?.();
  };

  const handleVoucherRedeem = () => {
    setIsMenuOpen(false);
    onVoucherRedeemClick?.();
  };

  const handleInviteClick = () => {
    setIsMenuOpen(false);
    onInviteClick?.();
  };

  const shouldShowGuestQuota = !isAuthenticated && guestQuotaStatus && guestQuotaStatus !== 'idle';
  const guestQuotaLabel =
    guestQuotaStatus === 'ready' && typeof guestQuotaRemaining === 'number'
      ? guestQuotaRemaining
      : guestQuotaStatus === 'error'
        ? '?'
        : null;

  const profileQuotaLabel =
    profile?.quotaStatus === 'ready' && typeof profile.quotaRemaining === 'number'
      ? profile.quotaRemaining
      : profile?.quotaStatus === 'error'
        ? '?'
        : null;

  return (
    <div className="relative min-h-screen bg-ai-surface text-ai-text-primary">
      <SEOHead />
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute inset-x-0 top-[-40vh] h-[60vh] bg-ai-radial blur-3xl" />
        <div className="absolute inset-0 bg-ai-grid bg-grid-lg opacity-40" />
      </div>

      <div className="relative flex min-h-screen flex-col px-6 pb-12 pt-2 sm:px-10 lg:px-3">
        <header className="flex flex-col gap-4 pb-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-2">
            <h1 className="font-display text-3xl font-semibold sm:text-xl">
              {t('app.title')}
            </h1>
          </div>
          <div className="flex flex-wrap items-center gap-3 text-sm text-ai-text-secondary sm:justify-end">
            {isAuthenticated ? (
              <div className="relative" ref={menuRef}>
                <button
                  type="button"
                  className={actionButtonClasses}
                  onClick={handleToggleMenu}
                  aria-haspopup="menu"
                  aria-expanded={isMenuOpen}
                >
                  {t('app.actions.my')}
                </button>
                {isMenuOpen ? (
                  <div
                    className={clsx(
                      'absolute z-50 mt-2 rounded-2xl border border-ai-surface-glow/40 bg-ai-surface-elevated/90 p-4 shadow-ai-card',
                      'left-0 w-[min(18rem,calc(100vw-2.5rem))]',
                      'sm:left-auto sm:right-0 sm:w-64'
                    )}
                  >
                    <dl className="space-y-2 text-sm text-ai-text-primary">
                      <div>
                        <dt className="text-[0.65rem] uppercase tracking-[0.3em] text-ai-text-secondary sm:text-xs">
                          {t('app.profile.email')}
                        </dt>
                        <dd className="mt-0.5 break-all text-sm">{displayEmail}</dd>
                      </div>
                      <div>
                        <dt className="text-[0.65rem] uppercase tracking-[0.3em] text-ai-text-secondary sm:text-xs">
                          {t('app.profile.username')}
                        </dt>
                        <dd className="mt-0.5 break-all text-sm">{displayName}</dd>
                      </div>
                      <div>
                        <dt className="text-[0.65rem] uppercase tracking-[0.3em] text-ai-text-secondary sm:text-xs">
                          {t('app.profile.quota')}
                        </dt>
                        <dd className="mt-0.5 text-sm">
                          {profileQuotaLabel ?? t('app.profile.unknown')}
                        </dd>
                      </div>
                    </dl>
                    {onInviteClick ? (
                      <button
                        type="button"
                        className="mt-4 inline-flex w-full items-center justify-center rounded-full border border-ai-primary/60 px-4 py-2 text-xs font-semibold uppercase tracking-[0.35em] text-ai-text-primary transition-colors hover:border-ai-primary focus:outline-none focus-visible:ring focus-visible:ring-ai-focus"
                        onClick={handleInviteClick}
                      >
                        {t('memberPage.invite.label')}
                      </button>
                    ) : null}
                    {onAddFundsClick ? (
                      <button
                        type="button"
                        className={clsx(
                          voucherHighlightClasses,
                          'mt-3 w-full justify-center text-xs tracking-[0.35em]'
                        )}
                        onClick={() => {
                          setIsMenuOpen(false);
                          onAddFundsClick();
                        }}
                      >
                        {t('payment.title')}
                      </button>
                    ) : null}
                    {onVoucherRedeemClick ? (
                      <button
                        type="button"
                        className="mt-3 inline-flex w-full items-center justify-center text-[0.7rem] font-semibold uppercase tracking-[0.3em] text-ai-text-secondary/70 transition-colors hover:text-ai-text-primary focus:outline-none focus-visible:ring focus-visible:ring-ai-focus"
                        onClick={handleVoucherRedeem}
                      >
                        {t('app.actions.redeemVoucherLoggedIn')}
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="mt-3 inline-flex w-full items-center justify-center text-[0.7rem] font-semibold uppercase tracking-[0.3em] text-ai-text-secondary/70 transition-colors hover:text-ai-text-primary focus:outline-none focus-visible:ring focus-visible:ring-ai-focus"
                      onClick={handleSignOut}
                    >
                      {t('memberPage.auth.signOut')}
                    </button>
                  </div>
                ) : null}
              </div>
            ) : onLoginClick ? (
              <div className="flex flex-wrap items-center gap-2 text-sm">
                {shouldShowGuestQuota && guestQuotaLabel !== null ? (
                  <>
                    <span className="inline-flex items-center gap-1 rounded-full bg-ai-surface/80 px-3 py-1 text-xs font-semibold text-ai-text-secondary">
                      {t('app.actions.remainingDeviceQuota', { remaining: guestQuotaLabel })}
                    </span>
                    <span className="text-ai-text-secondary/70">·</span>
                  </>
                ) : null}
                <button
                  type="button"
                  className={actionButtonClasses}
                  onClick={onLoginClick}
                  aria-haspopup="dialog"
                  aria-expanded={isLoginActive}
                >
                  <Trans
                    i18nKey="app.actions.redeemLogin"
                    components={{
                      redeem: <span className={voucherHighlightClasses} />,
                      login: <span className="text-ai-text-secondary" />
                    }}
                  />
                </button>
              </div>
            ) : null}
            <LanguageSwitcher />
          </div>
        </header>

        <main
          className={clsx(
            'relative flex w-screen flex-1 flex-col gap-2',
            '-mx-6 sm:-mx-10 lg:-mx-3',
            'rounded-glass-lg border border-ai-surface-glow/30 bg-ai-glass p-2 shadow-ai-card',
            'backdrop-blur-2xl'
          )}
        >
          {children}
        </main>

        <footer className="mt-10 flex flex-col items-start gap-2 border-t border-ai-surface-glow/30 pt-6 text-xs text-ai-text-secondary sm:flex-row sm:items-center sm:justify-between">
          <span>{t('app.footer.copyright', { year: new Date().getFullYear() })}</span>
          <div className="flex flex-wrap items-center gap-3 text-ai-text-secondary">
            <span className="inline-flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-ai-accent" />
              {t('app.footer.tagline')}
            </span>
            <span className="font-mono text-xs text-ai-text-secondary/80">
              {t('app.footer.version', { version: appVersion })}
            </span>
          </div>
        </footer>
      </div>
    </div>
  );
}
